import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, catchError, map, mergeMap, exhaustMap, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { createTicketType, createTicketTypeFailure, 
  createTicketTypeSuccess, deleteTicketType, deleteTicketTypeFailure,
  deleteTicketTypeSuccess, filterTicketJobTitleAssignments, filterTicketJobTitleAssignmentsSuccess, filterTicketRoleAssignments, filterTicketRoleAssignmentsSuccess, filterTicketUserAssignments, filterTicketUserAssignmentsSuccess,
  getFieldTypes,
  getFieldTypesSuccess,
  getProgramList,
  getProgramListSuccess,
  getViewTypes, getViewTypesSuccess, loadTicketType, loadTicketTypeFailure, loadTicketTypes, 
  loadTicketTypesFailure, loadTicketTypesSuccess, loadTicketTypeSuccess, reorderStatuses, 
  reorderStatusesFailure, reorderStatusesSuccess, saveTicketJobTitleAssignments, saveTicketRoleAssignments, saveTicketUserAssignments, saveTicketUserAssignmentsSuccess, updateTicketType, updateTicketTypeFailure, 
  updateTicketTypeSuccess } from './ticket-types.actions';
import { saveTicketRoleAssignmentsSuccess, saveTicketJobTitleAssignmentsSuccess, saveTicketAssignmentsFailure } from './ticket-types.actions';
import { TicketTypesService } from '../../services/ticket-types.service';
import { Store } from '@ngrx/store';
import { State } from '..';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ActivatedRoute, Router } from '@angular/router';


@Injectable()
export class TicketTypesEffects {
    constructor(private actions$: Actions,
      private ticketTypesService: TicketTypesService,
      private alertService: AlertService,
      private store: Store<State>,
      private router: Router,
    ) { }

  saveSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(createTicketTypeSuccess, updateTicketTypeSuccess, saveTicketUserAssignmentsSuccess,
      saveTicketRoleAssignmentsSuccess, saveTicketJobTitleAssignmentsSuccess),
    tap(() => this.alertService.success('Ticket type saved'))
  ), { dispatch: false });

  requestFailure$ = createEffect(() => this.actions$.pipe(
    ofType(loadTicketTypeFailure, createTicketTypeFailure, updateTicketTypeFailure, deleteTicketTypeFailure, saveTicketAssignmentsFailure),
    tap(action => {
      // ErrorInterceptor rethrows the response body (or the whole response when there is no body) as a JSON string.
      let details = action.error;
      if (typeof details === 'string') {
        try { details = JSON.parse(details); } catch { }
      }
      const forbidden = details?.status === 403;
      if (details && typeof details === 'object' && 'error' in details) details = details.error;
      const message = forbidden ? 'You do not have permission to make this change.'
        : typeof details === 'string' ? details
        : details?.errors ? Object.values(details.errors).map(value => Array.isArray(value) ? value.join(' ') : value).join(' ')
        : details?.detail;
      this.alertService.error(message || (action.type === loadTicketTypeFailure.type
        ? 'Unable to load the ticket type. Return to Ticket Types and try again.'
        : 'Unable to save the ticket type. Please try again.'));
    })
  ), { dispatch: false });

  getState(store: Store<State>): State {
    let state: State;

    store.pipe(take(1)).subscribe(s => state = s);

    return state;
  }

  loadTicketTypes$ = createEffect(() => this.actions$.pipe(
    ofType(loadTicketTypes, deleteTicketTypeSuccess),
    withLatestFrom(this.store.select(s => s.ticketTypesState.includeArchived)),
    switchMap(([, includeArchived]) => this.ticketTypesService.getAllTicketTypes(includeArchived).pipe(
      map(ticketTypes => loadTicketTypesSuccess({ ticketTypes, includeArchived })),
      catchError(error => of(loadTicketTypesFailure({ error })))
    ))
  ));

  loadTicketType$ = createEffect(() => this.actions$.pipe(
    ofType(loadTicketType),
    switchMap(action => this.ticketTypesService.getTicketTypeById(action.id).pipe(
      map(ticketType => loadTicketTypeSuccess({ ticketType })),
      catchError(error => of(loadTicketTypeFailure({ error })))
    ))
  ));

  createTicketType$ = createEffect(() => this.actions$.pipe(
    ofType(createTicketType),
    exhaustMap(action => this.ticketTypesService.createTicketType(action.ticketType).pipe(
      map(ticketType => createTicketTypeSuccess({ ticketType })),
      catchError(error => of(createTicketTypeFailure({ error })))
    ))
  ));

  updateTicketType$ = createEffect(() => this.actions$.pipe(
    ofType(updateTicketType),
    exhaustMap(action => this.ticketTypesService.updateTicketType(action.id, action.ticketType).pipe(
      map(ticketType => updateTicketTypeSuccess({ ticketType })),
      catchError(error => of(updateTicketTypeFailure({ error })))
    ))
  ));
    
  deleteTicketType$ = createEffect(() => this.actions$.pipe(
    ofType(deleteTicketType),
    exhaustMap(action => this.ticketTypesService.deleteTicketType(action.id, action.archived).pipe(
      map(ticketType => deleteTicketTypeSuccess({ ticketType })),
      catchError(error => of(deleteTicketTypeFailure({ error })))
    ))
  ));

  deleteTicketTypeSuccess = createEffect(() => this.actions$.pipe(
    ofType(deleteTicketTypeSuccess),
    tap(action => {
      this.alertService.success(
        action.ticketType.archivedDate ? 'Ticket type archived' : 'Ticket type unarchived', false);
    })
  ), { dispatch: false });
    reorderStatuses$ = createEffect(() => this.actions$.pipe(
      ofType(reorderStatuses),
      mergeMap(action => this.ticketTypesService.reorderStatuses(action.id, action.startIndex, action.newIndex).pipe(
        map((statuses) => reorderStatusesSuccess({ statuses: statuses })),
        catchError(error => of(reorderStatusesFailure({ error })))
      ))
    ));
  
    getViewTypes$ = createEffect(() => this.actions$.pipe(
      ofType(getViewTypes),
      mergeMap(() => this.ticketTypesService.getViewTypes().pipe(
        map(viewTypes => getViewTypesSuccess({ viewTypes }))
      ))
    ));
  
    getProgramList$ = createEffect(() => this.actions$.pipe(
      ofType(getProgramList),
      mergeMap(() => this.ticketTypesService.getProgramList().pipe(
        map(programs => getProgramListSuccess({ programs }))
      ))
    ));
    
    getFieldTypes$ = createEffect(() => this.actions$.pipe(
      ofType(getFieldTypes),
      mergeMap(() => this.ticketTypesService.getFieldTypes().pipe(
        map(viewTypes => getFieldTypesSuccess({ viewTypes }))
      ))
    ));
  
    filterTicketUserAssignments$ = createEffect(() => this.actions$.pipe(
      ofType(filterTicketUserAssignments),
      switchMap((action) => this.ticketTypesService.filterTicketUserAssignments(action.filter).pipe(
        map(userAssignments => filterTicketUserAssignmentsSuccess({ ticketTypeID: action.filter.ticketTypeID, userAssignments }))
      ))
    ));
  
    filterTicketRoleAssignments$ = createEffect(() => this.actions$.pipe(
      ofType(filterTicketRoleAssignments),
      switchMap((action) => this.ticketTypesService.filterTicketRoleAssignments(action.filter).pipe(
        map(roleAssignments => filterTicketRoleAssignmentsSuccess({ ticketTypeID: action.filter.ticketTypeID, roleAssignments }))
      ))
    ));
      
    filterTicketJobTitleAssignments$ = createEffect(() => this.actions$.pipe(
      ofType(filterTicketJobTitleAssignments),
      switchMap((action) => this.ticketTypesService.filterTicketJobTitleAssignments(action.filter).pipe(
        map(jobTitleAssignments => filterTicketJobTitleAssignmentsSuccess({ ticketTypeID: action.filter.ticketTypeID, jobTitleAssignments }))
      ))
    ));
      
    saveTicketUserAssignments$ = createEffect(() => this.actions$.pipe(
      ofType(saveTicketUserAssignments),
      exhaustMap((action) => this.ticketTypesService.saveTicketUserAssignments(action.ticketTypeID, action.users).pipe(
        map(userAssignments => saveTicketUserAssignmentsSuccess({ ticketTypeID: action.ticketTypeID, userAssignments })),
        catchError(error => of(saveTicketAssignmentsFailure({ error })))
      ))
    ));
  
    saveTicketRoleAssignments$ = createEffect(() => this.actions$.pipe(
      ofType(saveTicketRoleAssignments),
      exhaustMap((action) => this.ticketTypesService.saveTicketRoleAssignments(action.ticketTypeID, action.roles).pipe(
        map(roleAssignments => saveTicketRoleAssignmentsSuccess({ ticketTypeID: action.ticketTypeID, roleAssignments })),
        catchError(error => of(saveTicketAssignmentsFailure({ error })))
      ))
    ));
      
    saveTicketJobTitleAssignments$ = createEffect(() => this.actions$.pipe(
      ofType(saveTicketJobTitleAssignments),
      exhaustMap((action) => this.ticketTypesService.saveTicketJobTitleAssignments(action.ticketTypeID, action.jobTitles).pipe(
        map(jobTitleAssignments => saveTicketJobTitleAssignmentsSuccess({ ticketTypeID: action.ticketTypeID, jobTitleAssignments })),
        catchError(error => of(saveTicketAssignmentsFailure({ error })))
      ))
    ));
}
